import type {
  CloseAiChatPanelProperties,
  SendPromptToAiProperties,
} from "@/analytics/ai-chat-events-properties";
import { EventType } from "@/analytics/analytics-events";
import {
  AI_GRADIENT,
  AI_GRADIENT_SMALL,
} from "@/components/common/ai-assets/ai-gradients";
import { runtimeConfig } from "@/runtime-config";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { useChat } from "@ai-sdk/react";
import {
  Banner,
  blue,
  CloseIcon,
  codeToLanguageCodes,
  FaroButton,
  FaroIconButton,
  FaroText,
  grey,
  HelpIcon,
  NO_TRANSLATE_CLASS,
  TextField,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import {
  Analytics,
  useLocalizeLanguage,
  useTranslate,
} from "@faro-lotv/foreign-observers";
import { selectProjectId } from "@faro-lotv/project-source";
import {
  useApiClientContext,
  type ConversationFeedback,
} from "@faro-lotv/service-wires";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, keyframes, Paper, Stack } from "@mui/material";
import { generateId, isTextUIPart } from "ai";
import {
  useCallback,
  useMemo,
  useState,
  type JSX,
  type PropsWithChildren,
} from "react";
import { selectIsAiModelUpdating } from "./ai-selectors";
import { setIsAiChatOpen } from "./ai-slice";
import { AssistantOutput } from "./assistant-output";
import { useAutoScroll } from "./use-auto-scroll";
import { VlmAnalysisTool } from "./vlm-analysis-tool";
import { WebSocketChatTransport } from "./web-socket-chat-transport";

/**
 * @returns An AI chat
 */
export function AiChat(): JSX.Element {
  const dispatch = useAppDispatch();

  const projectId = useAppSelector(selectProjectId);
  const currentUser = useAppSelector(selectCurrentUser);

  const shouldShowVlmAnalysis = useAppSelector(
    selectHasFeature(Features.VlmAnalysis),
  );

  const isAiModelUpdating = useAppSelector(selectIsAiModelUpdating);

  const { tokenProvider, projectApiClient } = useApiClientContext();

  const [chatId, setChatId] = useState(generateId());

  const { messages, setMessages, sendMessage, status } = useChat({
    id: chatId,
    transport: new WebSocketChatTransport({
      api: `${runtimeConfig.backendEndpoints.projectApiUrl}/v1/ai/${projectId}/chat`,
      token: tokenProvider,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  });

  const [query, setQuery] = useState<string>("");

  const {
    scrollContainerRef,
    showScrollButton,
    scrollToBottom,
    onScroll,
    resetAutoScroll,
  } = useAutoScroll([messages]);

  const queryAi = useCallback(
    (text: string, via: SendPromptToAiProperties["via"]) => {
      Analytics.track<SendPromptToAiProperties>(EventType.sendPromptToAi, {
        via,
      });

      sendMessage({ text });
      setQuery("");
    },
    [sendMessage],
  );

  const startNewChat = useCallback(() => {
    Analytics.track(EventType.clearCurrentAiChatHistory);

    setChatId(generateId());

    setMessages([]);
    setQuery("");
    resetAutoScroll();
  }, [setMessages, resetAutoScroll]);

  const closeChat = useCallback(() => {
    Analytics.track<CloseAiChatPanelProperties>(EventType.closeAiChatPanel, {
      via: "chat panel",
    });

    dispatch(setIsAiChatOpen(false));
  }, [dispatch]);

  const provideMessageFeedback = useCallback(
    async (feedback: ConversationFeedback) => {
      await projectApiClient.provideAiConversationFeedback(chatId, feedback);
    },
    [projectApiClient, chatId],
  );

  const language = useLocalizeLanguage();
  const helpCenterLanguage = codeToLanguageCodes(language.code);

  const openAiHelp = useCallback(() => {
    Analytics.track(EventType.clickOnAiHelpIcon);

    void window.open(
      `https://help.holobuilder.com/${helpCenterLanguage}/articles/14891668-get-project-information-with-the-faro-ai-assistant`,
      "_blank",
    );
  }, [helpCenterLanguage]);

  let greetingText = <>How can I help?</>;
  if (currentUser?.firstName) {
    greetingText = (
      <>
        How can I help,{" "}
        <TranslateVar name="firstName">{currentUser.firstName}</TranslateVar>?
      </>
    );
  }

  return (
    <Stack
      sx={{
        width: "25%",
        borderLeft: `1px ${grey[200]} solid`,
        minWidth: "400px",
        gap: 2,
        alignContent: "space-between",
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
          gap: 1,
          height: "40px",
          borderBottom: `1px ${grey[200]} solid`,
          p: 1,
          pr: 2,
        }}
      >
        {shouldShowVlmAnalysis && <VlmAnalysisTool />}

        <FaroIconButton size="xs" onClick={openAiHelp}>
          <HelpIcon />
        </FaroIconButton>

        <FaroIconButton size="xs" onClick={closeChat}>
          <CloseIcon />
        </FaroIconButton>
      </Stack>

      <Stack
        sx={{
          py: 2,
          px: 1,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ position: "relative", flexGrow: 1, minHeight: 0 }}>
          <Stack
            ref={scrollContainerRef}
            onScroll={onScroll}
            sx={{
              overflow: "scroll",
              height: "100%",
            }}
            // LLM output shouldn't be sent to localize, and LLM will take input language into consideration for output language
            className={NO_TRANSLATE_CLASS}
          >
            {messages.map((message) => {
              if (message.role === "user") {
                return (
                  <UserPrompt key={message.id}>
                    {message.parts.find(isTextUIPart)?.text}
                  </UserPrompt>
                );
              }

              // message.role === "assistant"
              return (
                <AssistantOutput
                  key={message.id}
                  message={message}
                  onVote={provideMessageFeedback}
                />
              );
            })}

            {status === "streaming" && <TypingIndicator />}
          </Stack>

          {showScrollButton && (
            <FaroIconButton
              onClick={scrollToBottom}
              size="s"
              sx={{
                position: "absolute",
                bottom: 8,
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: grey[100],
                boxShadow: 2,
                "&:hover": {
                  backgroundColor: grey[200],
                },
              }}
            >
              <KeyboardArrowDownIcon />
            </FaroIconButton>
          )}
        </Box>

        {!messages.length && (
          <Stack
            direction="column"
            sx={{
              justifyContent: "center",
              gap: 2,
              pb: 10,
              px: 3,
            }}
          >
            <FaroText variant="heading20" sx={{ textAlign: "center" }}>
              {greetingText}
            </FaroText>
            <FaroText variant="heading14" sx={{ textAlign: "center", pb: 3 }}>
              Not sure where to start? Click one of the options below.
            </FaroText>
            <PromptButton onClick={queryAi}>
              Summarize the condition of this facility.
            </PromptButton>
            <PromptButton onClick={queryAi}>
              Show the 10 most recent captures.
            </PromptButton>
            <PromptButton onClick={queryAi}>
              What are the top five safety concerns?
            </PromptButton>
          </Stack>
        )}

        <Stack sx={{ gap: 2 }}>
          {isAiModelUpdating && (
            <Banner variant="info">
              Your project data is still being processed, so answers might not
              reflect the latest changes yet. Check progress in Cloud Activity.
            </Banner>
          )}
          <TextField
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                queryAi(query, "manual input");
              }
            }}
            disabled={status !== "ready"}
            fullWidth
            multiline
            autoFocus
            rows={2}
            text={query}
            onTextChanged={setQuery}
          />

          <Stack
            direction="row"
            sx={{ gap: 0.5, alignContent: "space-between", alignItems: "end" }}
          >
            <FaroButton
              disabled={status !== "ready" || !query}
              onClick={() => queryAi(query, "manual input")}
              sx={{
                marginBottom: "5px",
                width: "80%",
                color: grey[0],
                ...AI_GRADIENT,
                "&:hover": {
                  border: `1px ${blue[600]} solid`,
                  filter: "brightness(0.95)",
                },
                "&:disabled": {
                  color: grey[0],
                },
              }}
            >
              Ask
            </FaroButton>
            <FaroButton
              disabled={status !== "ready" && status !== "error"}
              onClick={startNewChat}
              variant="ghost"
              sx={{ marginBottom: "5px" }}
            >
              Clear
            </FaroButton>
          </Stack>

          <FaroText variant="placeholder" sx={{ textAlign: "center" }}>
            Uses AI. Always verify results.
          </FaroText>
        </Stack>
      </Stack>
    </Stack>
  );
}

type PromptButtonProps = {
  children: string;
  onClick(prompt: string, via: SendPromptToAiProperties["via"]): void;
};

const backgroundAnimation = keyframes`
  0% {background-position: 0% 50%}
  100% {background-position: 100% 50%}
`;

function PromptButton({ onClick, children }: PromptButtonProps): JSX.Element {
  const translate = useTranslate();
  const translatedPrompt = translate(children);
  return (
    <FaroButton
      variant="secondary"
      className={NO_TRANSLATE_CLASS}
      sx={{
        height: "auto",

        "&:hover": {
          color: grey[200],
          animation: `${backgroundAnimation} alternate 1.5s infinite`,
          ...AI_GRADIENT_SMALL,
          backgroundSize: "300% 300%",
        },
      }}
      onClick={() => onClick(translatedPrompt, "example prompt")}
    >
      {translatedPrompt}
    </FaroButton>
  );
}

/**
 * @returns a UI to highlight the prompt input by the user inside the chat history
 */
function UserPrompt({ children }: PropsWithChildren): JSX.Element {
  return (
    <Paper
      sx={{
        textAlign: "left",
        maxWidth: "90%",
        py: 1,
        px: 2,
        borderRadius: 4,
        borderTopRightRadius: 0,
        alignSelf: "end",
      }}
    >
      {children}
    </Paper>
  );
}

/**
 * @returns A typing indicator with three animated dots
 */
function TypingIndicator(): JSX.Element {
  const bounce = useMemo(
    () => keyframes`
      0%, 60%, 100% {
        transform: translateY(0);
      }
      30% {
        transform: translateY(-8px);
      }
    `,
    [],
  );

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 0.5, py: 2 }}>
      {[0, 1, 2].map((index) => (
        <Box
          component="div"
          key={index}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: blue[500],
            animation: `${bounce} 1.4s infinite ease-in-out`,
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            animationDelay: `${index * 0.16}s`,
          }}
        />
      ))}
    </Stack>
  );
}
